import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  createContext,
  useContext,
  Children,
  type ReactNode,
} from 'react'
import * as m from 'motion/react-m'
import { type PanInfo } from 'motion/react'
import { MotionLayout } from '@/lib/motion/lazy-motion'

import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'

type SliderProps = {
  children: ReactNode
  className?: string
  autoPlay?: boolean
  interval?: number
  reduced?: boolean
}

const SLIDE_GAP = 100 // percentage offset for side items

const normalTransition = {
  x: { type: 'tween' as const, duration: 0.45, ease: [0.32, 0.72, 0, 1] as const },
  opacity: { duration: 0.3, ease: 'easeOut' as const },
  scale: { duration: 0.45, ease: [0.34, 1.56, 0.64, 1] as const },
}

const instantTransition = { duration: 0 }

// Lets descendants (e.g. an open Menu/Popover) pause autoplay while interacting. The
// hover-pause alone is defeated by the Popover's full-screen overlay stealing the hover.
type SliderPause = { pause: () => void; resume: () => void }
const SliderPauseContext = createContext<SliderPause | null>(null)
export function useSliderPause(): SliderPause | null {
  return useContext(SliderPauseContext)
}

export default function Slider({
  children,
  className,
  autoPlay = true,
  interval = 3000,
  reduced = false,
}: SliderProps) {
  const childrenArray = Children.toArray(children)
  const total = childrenArray.length
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [childPauseCount, setChildPauseCount] = useState(0)
  const pauseApi = useMemo<SliderPause>(
    () => ({
      pause: () => setChildPauseCount((c) => c + 1),
      resume: () => setChildPauseCount((c) => Math.max(0, c - 1)),
    }),
    [],
  )
  const [slideWidth, setSlideWidth] = useState(0)
  const [autoplayKey, setAutoplayKey] = useState(0)
  const prevOffsetsRef = useRef<Map<number, number>>(new Map())
  const isIntentionalJumpRef = useRef(false)
  // Framer only fires onDragStart once real pointer movement is recognized
  // (not on a plain tap), so this reliably distinguishes "click" from
  // "click+drag". The click event fires after onDragEnd, so checking it in
  // onClickCapture lets us swallow navigation only when a drag happened.
  const hasDraggedRef = useRef(false)

  if (total === 0) return null

  useEffect(() => {
    const calculateWidth = () => {
      const maxWidth = 520
      const viewportWidth = window.innerWidth

      let width
      if (viewportWidth < 768) {
        width = Math.min(viewportWidth * 0.8, maxWidth)
      } else if (viewportWidth < 1024) {
        width = Math.min(viewportWidth * 0.6, maxWidth)
      } else {
        width = Math.min(viewportWidth * 0.33333, maxWidth)
      }

      setSlideWidth(width)
    }

    calculateWidth()
    window.addEventListener('resize', calculateWidth)
    return () => window.removeEventListener('resize', calculateWidth)
  }, [])

  const swipeTo = useCallback(
    (dir: number, isManual = false) => {
      setCurrentIndex((prev) => {
        const newIndex = prev + dir
        if (newIndex >= total) return 0
        if (newIndex < 0) return total - 1
        return newIndex
      })

      if (isManual) {
        setAutoplayKey((prev) => prev + 1)
      }
    },
    [total],
  )

  const goToSlide = useCallback((index: number) => {
    isIntentionalJumpRef.current = true
    setCurrentIndex(index)
    setAutoplayKey((prev) => prev + 1)
    // Reset the flag after the animation completes
    setTimeout(() => {
      isIntentionalJumpRef.current = false
    }, 500)
  }, [])

  useEffect(() => {
    if (!autoPlay || isPaused || childPauseCount > 0) return

    const timer = setInterval(() => {
      swipeTo(1, false)
    }, interval)

    return () => clearInterval(timer)
  }, [autoPlay, interval, isPaused, childPauseCount, swipeTo, autoplayKey])

  // Circular offset: shortest path around the ring
  function getOffset(index: number): number {
    const diff = index - currentIndex
    const halfTotal = total / 2
    if (diff > halfTotal) return diff - total
    if (diff < -halfTotal) return diff + total
    return diff
  }

  // Update previous offsets after each render
  useEffect(() => {
    const offsets = new Map<number, number>()
    for (let i = 0; i < total; i++) {
      offsets.set(i, getOffset(i))
    }
    prevOffsetsRef.current = offsets
  })

  if (slideWidth === 0) return null

  return (
    <SliderPauseContext.Provider value={pauseApi}>
      <MotionLayout>
        <div onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          <div
            className={cn(
              'relative mx-auto h-[420px] w-screen overflow-hidden px-4 sm:px-8',
              reduced &&
                'before:absolute before:left-0 before:top-0 before:z-30 before:h-full before:w-5 before:bg-gradient-to-r before:from-black before:to-black/0 before:content-[""] after:absolute after:right-0 after:top-0 after:z-30 after:h-full after:w-5 after:bg-gradient-to-l after:from-black after:to-black/0 after:content-[""] md:before:w-20 md:after:w-20',
              className,
            )}
            style={{ maxWidth: reduced ? '800px' : '1600px' }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              {childrenArray.map((child, index) => {
                const offset = getOffset(index)
                const isActive = offset === 0
                const isVisible = Math.abs(offset) <= 1

                // Detect wrap-around: offset jumped by more than 1 position
                // But exclude intentional jumps from pagination clicks
                const prevOffset = prevOffsetsRef.current.get(index)
                const isWrapping =
                  !isIntentionalJumpRef.current &&
                  prevOffset !== undefined &&
                  Math.abs(offset - prevOffset) > 1

                return (
                  <m.div
                    key={index}
                    className="absolute flex h-full cursor-grab items-center justify-center"
                    initial={false}
                    animate={{
                      x: reduced ? offset * 320 : `${offset * SLIDE_GAP}%`,
                      scale: isActive ? 1 : 0.85,
                      opacity: isVisible ? (isActive ? 1 : 0.6) : 0,
                    }}
                    style={{
                      width: Math.max(slideWidth - 32, 0) + 'px',
                      zIndex: isActive ? 10 : 0,
                      pointerEvents: isActive ? 'auto' : 'none',
                    }}
                    transition={isWrapping ? instantTransition : normalTransition}
                    drag="x"
                    dragConstraints={{
                      left: -slideWidth * 0.5,
                      right: slideWidth * 0.5,
                    }}
                    dragElastic={0.2}
                    dragTransition={{
                      bounceStiffness: 400,
                      bounceDamping: 25,
                    }}
                    whileHover={{
                      scale: isActive ? 1.02 : 0.9,
                    }}
                    whileDrag={{
                      cursor: 'grabbing',
                      scale: isActive ? 0.98 : 0.85,
                    }}
                    onDragStart={() => {
                      hasDraggedRef.current = true
                    }}
                    onDragEnd={(_, info: PanInfo) => {
                      const draggedDistance = info.offset.x
                      const dragVelocity = info.velocity.x
                      const swipeThreshold = slideWidth * 0.15

                      if (Math.abs(dragVelocity) > 800) {
                        swipeTo(dragVelocity < 0 ? 1 : -1, true)
                      } else if (Math.abs(draggedDistance) > swipeThreshold) {
                        swipeTo(draggedDistance < 0 ? 1 : -1, true)
                      }

                      // A touch swipe often ends with no synthesized click (unlike
                      // a mouse drag), which would leave hasDraggedRef stuck true
                      // and swallow the next real tap. Reset on the next macrotask
                      // so it still runs AFTER any post-drag click reaches
                      // onClickCapture (which suppresses that click and clears it).
                      setTimeout(() => {
                        hasDraggedRef.current = false
                      }, 0)
                    }}
                    onClickCapture={(e) => {
                      if (hasDraggedRef.current) {
                        e.preventDefault()
                        e.stopPropagation()
                        hasDraggedRef.current = false
                      }
                    }}
                  >
                    {child}
                  </m.div>
                )
              })}
            </div>
          </div>

          <SliderControls
            onPrev={() => swipeTo(-1, true)}
            onNext={() => swipeTo(1, true)}
            canPrev={true}
            canNext={true}
            currentIndex={currentIndex}
            totalItems={childrenArray.length}
            goToSlide={goToSlide}
          />
        </div>
      </MotionLayout>
    </SliderPauseContext.Provider>
  )
}

type SliderControlsProps = {
  onPrev: () => void
  onNext: () => void
  canPrev: boolean
  canNext: boolean
  currentIndex: number
  totalItems: number
  goToSlide: (index: number) => void
}

export function SliderControls({
  onPrev,
  onNext,
  canPrev,
  canNext,
  currentIndex,
  totalItems,
  goToSlide,
}: SliderControlsProps) {
  return (
    <div className="mx-auto mt-10 flex w-fit items-center gap-6">
      <button
        onClick={onPrev}
        type="button"
        aria-label="Previous slide"
        disabled={!canPrev}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-black/5 text-black transition-colors hover:bg-black/10 disabled:opacity-30 disabled:hover:bg-white/15 dark:bg-white/15 dark:text-white dark:hover:bg-white/30"
      >
        <Icon name="chevron-left" size={12} />
      </button>
      <div className="flex">
        {Array.from({ length: totalItems }).map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            aria-current={index === currentIndex}
            className="group flex h-6 w-6 cursor-pointer items-center justify-center"
          >
            <div
              className={cn(
                'h-1.5 w-1.5 rounded-full transition-colors',
                index === currentIndex
                  ? 'bg-primary-500'
                  : 'bg-black/10 group-hover:bg-black/20 dark:bg-white/10 dark:group-hover:bg-white/20',
              )}
            ></div>
          </button>
        ))}
      </div>
      <button
        onClick={onNext}
        type="button"
        aria-label="Next slide"
        disabled={!canNext}
        className="flex h-8 w-8 rotate-180 cursor-pointer items-center justify-center rounded-full bg-black/5 text-black transition-colors hover:bg-black/10 disabled:opacity-30 disabled:hover:bg-white/15 dark:bg-white/15 dark:text-white dark:hover:bg-white/30"
      >
        <Icon name="chevron-left" size={12} />
      </button>
    </div>
  )
}
